import { Alert, AlertStatus, Device, Location, statusToString } from 'model';
import { AlarmSeverityValues, toSeverity } from './Alarm';
import { UserRolePayload, toUserRole } from './Role';
import { LocationType } from 'model/Location';
import { Dictionary } from 'lodash';
import moment from 'moment-timezone';

export interface AlarmReducedResponse {
  id: number;
  severity: AlarmSeverityValues;
}

type StatusResponse = 'triggered' | 'resolved';

type Coords = {
  latitude: number;
  longitude: number;
}

type GeoLocationResponse = {
  coordinates?: Coords;
}

export interface AlertLocationResponse {
  address: string;
  address2: string;
  city: string;
  id: string;
  nickname: string;
  postalCode: string;
  state: string;
  timezone: string;
  country?: string;
  userRoles: UserRolePayload[];
  geoLocation?: GeoLocationResponse;
}

export interface AlertResponse {
  id: string;
  alarm: AlarmReducedResponse;
  deviceId: string;
  status: StatusResponse;
  reason: string;
  locationId: string;
  displayTitle: string;
  displayMessage: string;
  resolutionDate: string;
  updateAt: string;
  createAt: string;
  location?: AlertLocationResponse;
}

export interface AlertsResponse {
  items: AlertResponse[];
  total: number;
}

export interface AlertActionPayload {
  deviceId?: string;
  locationId?: string;
  alarmIds: number[];
  snoozeSeconds: number;
}

export interface CriticalAlertFeedback {
  property: string;
  value: boolean | number | null;
}

export interface AlertFeedbackPayloads {
  LEAK_ALERT_NORMAL_CONFIRM: CriticalAlertFeedback[];
}

export interface CriticalFeedbackapiPayload {
  feedback: CriticalAlertFeedback[];
}
export interface CriticalAlertFeedbackPayload {
  alertId: string;
  apiPayload: CriticalFeedbackapiPayload;
}


export enum AlertFeedbackTypes {
  LEAK_ALERT_NORMAL_CONFIRM = 'LEAK_ALERT_NORMAL_CONFIRM',
  LEAK_ALERT_NORMAL_DENY = 'LEAK_ALERT_NORMAL_DENY',
  LEAK_ALERT_NORMAL_NOT_SURE = 'LEAK_ALERT_NORMAL_NOT_SURE'
}

const alertFeedbackPayloads = {
  LEAK_ALERT_NORMAL_CONFIRM: [{property: "should_accept_as_normal", value: false }, {property: "cause", value: 0}],
  LEAK_ALERT_NORMAL_DENY: [{property: "should_accept_as_normal", value: true}, {property: "cause", value: 0}],
  LEAK_ALERT_NORMAL_NOT_SURE: [{property: "should_accept_as_normal", value: null}, {property: "cause", value: 0}]
};

export const alertFeedbackTypeToAlertPayload= (feedbackType: string) => alertFeedbackPayloads[feedbackType as keyof typeof alertFeedbackPayloads];

export const fromStatus = (status: AlertStatus): StatusResponse => {
  switch(status) {
    case AlertStatus.TRIGGERED:
      return 'triggered';
    case AlertStatus.RESOLVED:
      return 'resolved';
  }
}

export const toStatus = (status: StatusResponse): AlertStatus => {
  switch(status) {
    case 'triggered':
      return AlertStatus.TRIGGERED;
    case 'resolved':
      return AlertStatus.RESOLVED;
  }
}

export const toAlert = (response: AlertResponse, location?: Location): Alert => ({
  id: response.id,
  alarm: {
    id: response.alarm.id,
    severity: toSeverity(response.alarm.severity)
  },
  deviceId: response.deviceId,
  status: toStatus(response.status),
  reason: response.reason,
  location: location ? location : {
    id: response.locationId
  },
  displayTitle: response.displayTitle,
  displayMessage: response.displayMessage,
  resolutionDate: response.resolutionDate,
  updateAt: response.updateAt,
  createAt: response.createAt,
})

export const toAlertsWithLocationData = (response: AlertResponse[], userId: string): Alert[] => {
  return response.map(alert => {
    const userRole = toUserRole(alert.location?.userRoles || [], userId);
    return toAlert(alert, {
      id: alert.location?.id || '',
      address: alert.location?.address || '',
      address2: alert.location?.address2 || '',
      city: alert.location?.city || '',
      children: [],
      nickname: alert.location?.nickname || '',
      postalCode: alert.location?.postalCode || '',
      state: alert.location?.state || '',
      country: alert.location?.country || '',
      timezone: alert.location?.timezone || '',
      type: LocationType.UNIT,
      roles: userRole.roles,
      hasInheritedRolesOnly: userRole.isInherited
    });
  });
}

export const generateCsvFromAlerts = (alerts: AlertResponse[], deviceMap: Dictionary<Device>) => `Name,Device,Location,Date,Status\n${alerts.map(
  (alert: AlertResponse) => ([
    alert.displayTitle || '-',
    deviceMap[alert.deviceId].nickname || '-',
    alert.location?.nickname || alert.location?.address || '-',
    moment.utc(alert.createAt).tz(alert.location?.timezone || 'US/Pacific').format('L LT') || '-',
    statusToString(toStatus(alert.status)) || '-'
]).join(',')).join('\n')}`;
